import React from "react";
import html2pdf from "html2pdf.js";
import { flashMessage } from "../flash";
import {
  // getDaysInMonth,
  differenceInDays,
  parse,
  isLeapYear,
  getYear,
} from "date-fns";
import moment from "moment";

export const schedulePolicyRepayment = (
  startDate,
  endDate,
  inputInterval,
  premium,
  duration
) => {
  // Calculate the total number of days between start date and end date
  const currentDate = new Date(startDate);
  const targetDate = new Date(endDate);
  let totalDays = Math.ceil((targetDate - currentDate) / (1000 * 60 * 60 * 24));

  if (new Date().getMonth() === 0 || new Date().getMonth() === 1) {
    totalDays += 1;
  }

  // dates between start date and end date
  const interval = inputInterval?.toLowerCase();
  // Calculate the average amount per day
  const averageAmount = parseFloat(premium) / totalDays;

  // Define the schedule array
  const schedulePayment = [];
  let incrementFn;

  // Calculate the number of intervals (months, weeks, or days) based on the given interval
  let numIntervals = 0;
  let remainder = 0;
  let amountPayment = averageAmount;
  if (interval === "annually") {
    numIntervals = Math.floor(totalDays / 365);
    incrementFn = (date) => date.setFullYear(date.getFullYear() + 1);
    remainder = (premium % numIntervals) / (numIntervals - 1);
    amountPayment = Math.floor(premium / numIntervals) + remainder;
  } else if (interval === "biannually") {
    numIntervals = Math.floor((totalDays * 2) / 365);
    incrementFn = (date) => date.setMonth(date.getMonth() + 6);
    amountPayment = Math.floor(premium / numIntervals);
    remainder = (premium % numIntervals) / (numIntervals - 1);
    amountPayment = Math.floor(premium / numIntervals) + remainder;
  } else if (interval === "quarterly") {
    numIntervals = Math.ceil((totalDays * 4) / 365);
    incrementFn = (date) => date.setMonth(date.getMonth() + 3);
    amountPayment = Math.floor(premium / numIntervals);
    remainder = (premium % numIntervals) / (numIntervals - 1);
    amountPayment = Math.floor(premium / numIntervals) + remainder;
  } else if (interval === "monthly" || interval === "Monthly") {
    numIntervals = Math.floor(totalDays / 30);
    incrementFn = (date) => date.setMonth(date.getMonth() + 1);
    amountPayment = Math.floor(premium / numIntervals);
    remainder = (premium % numIntervals) / (numIntervals - 1);
    amountPayment += remainder;
  } else if (interval === "weekly") {
    numIntervals = Math.floor(totalDays / 7);
    incrementFn = (date) => date.setDate(date.getDate() + 7);
    amountPayment = Math.floor(premium / numIntervals);
    remainder = (premium % numIntervals) / (numIntervals - 1);
    amountPayment += remainder;
  } else if (interval === "daily") {
    numIntervals = totalDays;
    incrementFn = (date) => date.setDate(date.getDate() + 1);
    remainder = (premium % numIntervals) / (numIntervals - 1);
    amountPayment = Math.floor(premium / numIntervals) + remainder;

    // let amountToAddFromReminder =
  } else {
    console.error("Invalid interval provided.");
    return [];
  }

  // find remainder of the amount when divided by number of intervals

  let accumulatedAmount = 0;

  // Distribute the amount evenly across the intervals
  let i = 1;
  while (i <= numIntervals) {
    let amount = parseFloat(amountPayment.toFixed(2));

    if (i === numIntervals) {
      // Last date
      let newAmount = parseFloat(premium) - accumulatedAmount.toFixed(2);
      schedulePayment.push({
        date: new Date(currentDate),
        amount: newAmount.toFixed(2),
      });
      i++;
    } else {
      // Other dates
      schedulePayment.push({
        date: new Date(currentDate),
        amount: amount.toFixed(2),
      });
      accumulatedAmount += amount;
      i++;
    }

    incrementFn(currentDate);
  }

  return schedulePayment;
};

// sorting users by their staffs to return only users in that office.
export const sortUsersByOffice = (office_id, users) =>
  users
    ?.filter((item) => parseInt(item?.office_id) === office_id)
    ?.map((item) => ({
      label: item?.last_name + " " + item?.first_name,
      value: item?.staff_id,
    }));

// sorting policies by their staffs to return only policies in that office.
export const sortPoliciesByOffice = (office_id, policies) =>
  policies
    ?.filter((item) => parseInt(item?.office_id) === office_id)
    ?.map((item) => ({
      label: item?.policyName,
      value: item?.policyApplicationNumber,
    }));
export const sortPoliciesByStaff = (staff_id, policies) =>
  policies
    ?.filter((item) => item?.staff_id?.toString() === staff_id)
    ?.map((item) => ({
      label: item?.policyName,
      value: item?.policyApplicationNumber,
    }));

export const sortCustomersByStaff = (staff_id, customers) =>
  customers
    ?.filter((item) => item?.staff_id.toString() === staff_id)
    ?.map((item) => ({
      label:
        item?.first_name === null
          ? item?.business_name
          : item?.last_name + " " + item?.first_name,
      value: item?.customer_id,
    }));

export const sortCustomersByOffice = (office_id, customers) =>
  customers
    ?.filter((item) => parseInt(item?.branch_id) === office_id)
    ?.map((item) => ({
      label:
        item?.first_name === null
          ? item?.business_name
          : item?.last_name + " " + item?.first_name,
      value: item?.customer_id,
    }));

// export data to excel file
export const exportXlsxData = (values) => {
  const itemsWithValues = {};
  let arr = values?.map((item) => {
    for (const key in item) {
      if (item[key]) {
        let keyChange = key?.split("_")?.join(" ");
        itemsWithValues[keyChange] = item[key];
      }
    }
    return itemsWithValues;
  });

  return arr;
};

export const GenericPdfDownloader = ({ rootElementId, downloadFileName }) => {
  const convertToPdf = () => {
    const element = document.getElementById(rootElementId);
    const opt = {
      margin: [15, 10, 20, 10],
      filename: downloadFileName,
      image: { type: "jpeg", quality: 1 },
      pagebreak: {
        avoid: ["td", "tr", "div"],
        // mode: ["avoid-all", "css", "legacy"],
      },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  };

  return (
    <button
      type='button'
      onClick={convertToPdf}
      className='btn btn-secondary btn-sm rounded-3'
    >
      Download pdf
    </button>
  );
};

export const getSumAssured = (dataArray) => {
  let totalSumAssured = 0;

  dataArray?.forEach((item) => {
    totalSumAssured += parseFloat(item.sumInsured);
  });

  return totalSumAssured.toFixed(2);
};

// Function to handle the file download
export const handleDownload = async (fileUrl, index = "") => {
  if (fileUrl) {
    try {
      const response = await fetch(fileUrl);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = `document-${index}`; // You can specify the desired file name
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        flashMessage("error", "Error downloading file");
        console.error("Error downloading file");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }
};

export const schedule = (
  startDate,
  endDate,
  inputInterval,
  premium,
  payments
) => {
  // Calculate the total number of days between start date and end date
  const currentDate = new Date(startDate);
  const targetDate = new Date(endDate);
  let totalDays = Math.ceil((targetDate - currentDate) / (1000 * 60 * 60 * 24));

  if (new Date().getMonth() === 0 || new Date().getMonth() === 1) {
    totalDays += 1;
  }

  // dates between start date and end date
  const interval = inputInterval?.toLowerCase();
  // Calculate the average amount per day
  const averageAmount = parseFloat(premium) / totalDays;

  // caculater the total amount of payments
  const totalPaymentAmount = payments?.reduce((total, payment) => {
    return total + parseFloat(payment.amount_paid);
  }, 0);

  // Define the schedule array
  const schedulePayment = [];
  let incrementFn;

  // Calculate the number of intervals (months, weeks, or days) based on the given interval
  let numIntervals = 0;
  let remainder = 0;
  let amountPayment = averageAmount;
  if (interval === "annually") {
    numIntervals = Math.floor(totalDays / 365);
    incrementFn = (date) => date.setFullYear(date.getFullYear() + 1);
    remainder = (premium % numIntervals) / (numIntervals - 1);
    amountPayment = Math.floor(premium / numIntervals) + remainder;
  } else if (interval === "biannually") {
    numIntervals = Math.floor((totalDays * 2) / 365);
    incrementFn = (date) => date.setMonth(date.getMonth() + 6);
    amountPayment = Math.floor(premium / numIntervals);
    remainder = (premium % numIntervals) / (numIntervals - 1);
    amountPayment = Math.floor(premium / numIntervals) + remainder;
  } else if (interval === "quarterly") {
    numIntervals = Math.floor((totalDays * 4) / 365);
    incrementFn = (date) => date.setMonth(date.getMonth() + 3);
    amountPayment = Math.floor(parseFloat(premium) / numIntervals);
    remainder = (parseFloat(premium) % numIntervals) / (numIntervals - 1);
    amountPayment = Math.floor(parseFloat(premium) / numIntervals) + remainder;
  } else if (interval === "monthly") {
    numIntervals = Math.floor(totalDays / 30);
    incrementFn = (date) => date.setMonth(date.getMonth() + 1);
    amountPayment = Math.floor(parseFloat(premium) / numIntervals);
    remainder =
      numIntervals === 1
        ? 0
        : (parseFloat(premium) % numIntervals) / (numIntervals - 1);
    amountPayment = Math.floor(parseFloat(premium) / numIntervals) + remainder;
  } else if (interval === "weekly") {
    numIntervals = Math.floor(totalDays / 7);
    incrementFn = (date) => date.setDate(date.getDate() + 7);
    remainder = (premium % numIntervals) / (numIntervals - 1);
    amountPayment = Math.floor(premium / numIntervals) + remainder;
  } else if (interval === "daily") {
    numIntervals = totalDays;
    incrementFn = (date) => date.setDate(date.getDate() + 1);
    remainder = (premium % numIntervals) / (numIntervals - 1);
    amountPayment = Math.floor(premium / numIntervals) + remainder;
  } else if (interval === "one-off") {
    let oneOff = calculateOneOff(payments, premium);

    return [
      {
        expected_date: new Date(currentDate),
        amount: parseFloat(premium),
        payment_date: dateOfCompletePayment(payments, premium, 1)?.lastDate,
        actual_payment: parseFloat(oneOff),
        total_outstanding: premium - parseFloat(oneOff),
      },
    ];
  } else {
    console.error("Invalid interval provided.");
    return [];
  }

  // find remainder of the amount when divided by number of intervals

  let accumulatedAmount = 0;

  // Distribute the amount evenly across the intervals
  let i = 1;
  let paymentRemainder = 0;
  let partPaid = false;

  while (i <= numIntervals) {
    let amount = parseFloat(amountPayment.toFixed(2));
    let actualPayment;
    let totalOutstanding;
    let payment_date;
    if (i === numIntervals) {
      let newAmount =
        parseFloat(premium).toFixed(2) - accumulatedAmount.toFixed(2);
      if (parseFloat(paymentRemainder) < newAmount.toFixed(2)) {
        if (interval === "monthly" && numIntervals === 1) {
          actualPayment = parseFloat(newAmount);
          totalOutstanding = newAmount?.toFixed(2) - parseFloat(newAmount);
          payment_date = dateOfCompletePayment(
            payments,
            amount,
            i - 1,
            newAmount
          )?.lastDate;

          partPaid = true;
        } else {
          actualPayment = parseFloat(paymentRemainder);
          totalOutstanding =
            newAmount?.toFixed(2) - parseFloat(paymentRemainder);
          payment_date = dateOfCompletePayment(
            payments,
            amount,
            i - 1,
            newAmount
          )?.lastDate;

          partPaid = true;
        }
      } else {
        actualPayment = parseFloat(paymentRemainder);
        totalOutstanding = newAmount.toFixed(2) - parseFloat(paymentRemainder);
        partPaid = true;
        payment_date = payment_date = dateOfCompletePayment(
          payments,
          amount,
          i - 1,
          newAmount
        )?.lastDate;
        if (parseFloat(paymentRemainder) > 0) {
          actualPayment = newAmount.toFixed(2);
          totalOutstanding = 0;
        }
      }
      // Last date
      schedulePayment.push({
        expected_date: new Date(currentDate),
        amount: parseFloat(newAmount.toFixed(2)),
        payment_date,
        actual_payment: parseFloat(actualPayment),
        // total_outstanding: parseFloat(newAmount.toFixed(2)),
        total_outstanding: partPaid
          ? parseFloat(totalOutstanding)
          : parseFloat(newAmount.toFixed(2)),
      });
      i++;
    } else {
      if (i === 1) {
        if (parseFloat(totalPaymentAmount) < parseFloat(amount.toFixed(2))) {
          actualPayment = totalPaymentAmount;
          totalOutstanding =
            parseFloat(amount.toFixed(2)) - parseFloat(totalPaymentAmount);
          payment_date = dateOfCompletePayment(payments, amount, i)?.lastDate;
          partPaid = true;
        } else {
          actualPayment = parseFloat(amount.toFixed(2));
          totalOutstanding = 0;
          paymentRemainder =
            parseFloat(totalPaymentAmount) - parseFloat(amount.toFixed(2));
          partPaid = true;
          payment_date = dateOfCompletePayment(payments, amount, i)?.lastDate;
        }
      } else {
        if (parseFloat(paymentRemainder) < parseFloat(amount.toFixed(2))) {
          actualPayment = parseFloat(paymentRemainder);
          totalOutstanding =
            parseFloat(amount.toFixed(2)) - parseFloat(paymentRemainder);
          paymentRemainder = 0;
          payment_date = dateOfCompletePayment(payments, amount, i)?.lastDate;
          partPaid = true;
        } else {
          actualPayment = parseFloat(amount.toFixed(2));
          totalOutstanding = 0;
          paymentRemainder -= parseFloat(amount.toFixed(2));
          payment_date = dateOfCompletePayment(payments, amount, i)?.lastDate;
          partPaid = true;
        }
      }
      // Other dates
      schedulePayment.push({
        expected_date: new Date(currentDate),
        amount: parseFloat(amount.toFixed(2)),
        payment_date,
        actual_payment: actualPayment,
        total_outstanding: partPaid
          ? parseFloat(totalOutstanding.toFixed(2))
          : parseFloat(amount.toFixed(2)),
      });
      accumulatedAmount += amount;
      i++;
    }

    incrementFn(currentDate);
  }

  return schedulePayment;
};

// one-off calculation
function calculateOneOff(payments, premium) {
  let oneOff = 0;

  for (const payment of payments) {
    const amountPaid = parseFloat(payment.amount_paid);

    if (oneOff + amountPaid >= premium) {
      oneOff = premium;
      break; // Exit the loop when premium is reached
    } else {
      oneOff += amountPaid;
    }
  }

  return oneOff;
}

// Helper function to format date as "M/D/YYYY"
// function formatDate(date) {
//   const month = date.getMonth() + 1;
//   const day = date.getDate();
//   const year = date.getFullYear();
//   return `${month}/${day}/${year}`;
// }

//function checking for the date of the last payment equal or greater than the expected payment amount
const dateOfCompletePayment = (payments, amount, interval, lastAmount = 0) => {
  const actualAmount = parseFloat(amount * interval + lastAmount);

  const result = payments?.reduce(
    (accumulator, payment) => {
      if (accumulator.totalPayment >= actualAmount) {
        // If totalPayment is already >= actualAmount, skip further processing
        return accumulator;
      }

      const { payment_date, amount_paid } = payment;

      // Add the current payment amount to the accumulator
      accumulator.totalPayment += parseFloat(amount_paid);

      // If the total payment is greater than or equal to 700, update the lastDate
      if (accumulator.totalPayment >= actualAmount) {
        accumulator.lastDate = payment_date;
      }

      return accumulator;
    },
    { totalPayment: 0, lastDate: null }
  );
  return result;
};

//***** ROUND UP VALUE TO THE NEAREST TENS *****//
export const roundUpTens = (value) => {
  return Math.ceil(parseFloat(value) / 10) * 10;
};
//***** ROUND UP ENDS HERE *****//

//***** SPLIT BY _ AND JOIN BY " " COVERT SENTENCE TO FIRST LETTER CAPITALIZED  *****//
export const dejoinBy_ = (value) => {
  const string = value?.split("_").join(" ");
  return string?.charAt(0).toUpperCase() + string?.slice(1);
};
//***** ROUND UP ENDS HERE *****//

//***** ANNUAL PREMIUM CALCULATION *****//

export const calculateAnnualPremium = (
  effective_date,
  end_date,
  rate = 100,
  sum_assured
) => {
  // Parse the effective date and end date strings into Date objects
  const effectiveDate = parse(
    moment(effective_date).format("M-D-Y"),
    "MM-dd-yyyy",
    new Date()
  );

  const endDate = parse(
    moment(end_date).format("M-D-Y"),
    "MM-dd-yyyy",
    new Date()
  );
  const effectiveDateYear = getYear(new Date(effective_date));
  const endDateYear = getYear(new Date(end_date));

  // Calculate the number of days between the two dates
  const daysBetween = differenceInDays(endDate, effectiveDate);
  let premium;

  if (checkLeapYear(effectiveDateYear) || checkLeapYear(endDateYear)) {
    // calculation for leap years
    const annaulPremium = (parseFloat(sum_assured) * parseFloat(rate)) / 100;
    const dailyPremiumPayable = annaulPremium / 366;
    premium = Math.ceil(dailyPremiumPayable * daysBetween * 100) / 100;
  } else {
    // Calculation for non leap years
    const annaulPremium = (parseFloat(sum_assured) * parseFloat(rate)) / 100;
    const dailyPremiumPayable = annaulPremium / 365;
    premium = Math.ceil(dailyPremiumPayable * daysBetween * 100) / 100;
  }
  return premium;
};
//***** ROUND UP ENDS HERE *****//

const checkLeapYear = (year) => {
  return isLeapYear(new Date(year, 1, 1)); // Passing February 1st of the given year
};

// Recalculate the annual premium based on the duration.
// const recalculateAnnualPremium = (riskCovered) => {
//   const annualPremium = riskCovered.reduce();
// };

// check if any element of the arrays are equals
// const isEqual = (obj1, obj2) => {
//   return obj1?.id === obj2?.id;
// };

export const joinBy_ = (parameter) => {
  return parameter
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, "_");
};
